angular.module('bizmetryApp').service('BusinessEventsService', [
  '$http', '$interval', 'CONFIG', 'ReferenceDataService',
  function ($http, $interval, CONFIG, ReferenceDataService) {

    const BASE     = CONFIG.BIZMETRY_BACKEND_URL + '/v1/api/bizmetry-biz-events/biz-event';
    const LLM_BASE = CONFIG.BIZMETRY_BACKEND_URL + '/v1/api/bizmetry-llm/intellisense';

    // Two IntelliSense engines, same /start + /{jobId}/status contract and same result shape:
    //   DETERMINISTIC — bizmetry-llm (embeddings + scoring, ~1-3 s). Used to discover which
    //                   operations best match the frame type (wizard, pass 1).
    //   AGENT         — bizmetry-intellisense (LLM agent with BMEL tools, ~30-60 s). Used to
    //                   build the mappings: wizard pass 2 and the Biz Event Composer.
    const INTELLISENSE_ENGINES = {
      DETERMINISTIC: { base: LLM_BASE, pollMs: 500 },
      AGENT:         { base: CONFIG.BIZMETRY_BACKEND_URL + '/v1/api/bizmetry-ai-agents/intellisense', pollMs: 1500 }
    };
    this.INTELLISENSE_ENGINE = { DETERMINISTIC: 'DETERMINISTIC', AGENT: 'AGENT' };

    function _engine(engine) {
      return INTELLISENSE_ENGINES[engine] || INTELLISENSE_ENGINES.DETERMINISTIC;
    }

    // Delay between status polls for an engine — the agent moves slower, no need to hammer it
    this.intelliSensePollInterval = function (engine) {
      return _engine(engine).pollMs;
    };

    // Biz event grammars — the shape of the data a biz event captures, fixed for
    // the whole event. The catalog is reference data (BIZ_EVENT_GRAMMAR_TYPE:
    // label = the biz_event_trigger_id wire value, desc = its description).
    // Only REST-API is implemented for now; the rest show as "Coming soon".
    var self = this;
    var ENABLED_GRAMMARS = ['REST-API'];
    var GRAMMAR_ICONS = {
      'REST-API': 'api', 'JSON': 'data_object', 'CSV': 'table_chart',
      'DELIMITED': 'view_column', 'FIXED_WIDTH': 'format_align_justify'
    };
    this.REST_API_GRAMMAR = 'REST-API';

    // Display shape of one grammar — needs no catalog, so list rows and the edit
    // wizard's badge can use it straight from a biz_event_trigger_id. Memoized:
    // the same id always yields the same object, so it's safe in watched
    // bindings (ng-if compares by identity; a fresh object each digest never settles).
    var _grammarShapes = {};
    this.describeGrammar = function (id, description) {
      id = id || '';
      var key = id + '|' + (description || '');
      return _grammarShapes[key] || (_grammarShapes[key] = {
        id:          id,
        label:       id.replace(/_/g, ' '),
        description: description || '',
        icon:        GRAMMAR_ICONS[id] || 'bolt',
        css:         id.toLowerCase().replace(/[^a-z0-9]/g, ''),
        enabled:     ENABLED_GRAMMARS.indexOf(id) > -1
      });
    };

    var _grammarTypes = null;   // cached promise — the catalog doesn't change at runtime
    this.getGrammarTypes = function () {
      if (!_grammarTypes) {
        _grammarTypes = ReferenceDataService.getReferenceData('BIZ_EVENT_GRAMMAR_TYPE')
          .then(function (response) {
            return (response.data || [])
              .sort(function (a, b) { return a.refObjectId - b.refObjectId; })
              .map(function (r) { return self.describeGrammar(r.refObjectLabel, r.refObjectDesc); });
          })
          .catch(function (error) {
            _grammarTypes = null;   // let the next caller retry
            throw error;
          });
      }
      return _grammarTypes;
    };

    // A biz event's grammar, taken from its versions (all share it). Versions
    // without a trigger predate the field and are REST-API, same as the backend.
    this.getBizEventGrammar = function (event) {
      var versions = (event && event.versions) || [];
      if (!versions.length) { return null; }
      var withTrigger = versions.find(function (v) {
        return v.biz_event_trigger && v.biz_event_trigger.biz_event_trigger_id;
      });
      return withTrigger ? withTrigger.biz_event_trigger.biz_event_trigger_id : self.REST_API_GRAMMAR;
    };

    function getAccountId() {
      try {
        const user = JSON.parse(sessionStorage.getItem('user'));
        return user && user.id ? user.id : null;
      } catch (e) {
        return null;
      }
    }

    function getBearer() {
      return (
        sessionStorage.getItem('access_token') ||
        sessionStorage.getItem('token') ||
        (JSON.parse(sessionStorage.getItem('user') || '{}').token) ||
        null
      );
    }

    // Keep slim ASBO metadata for BMEL source lookups as well as frame targets.
    // Heavy schemas/samples are omitted to keep edge request inspection fast.
    const IS_ATTR_KEYS = ['id', 'name', 'description', 'resource_id', 'resource_type_id',
                          'column_metadata_name', 'scalar_attribute_type', 'isPrimaryKey', 'isResourceNameField'];

    function compactIntelliSensePayload(payload) {
      const cfg = payload && payload.template && payload.template.templateConfig;
      if (!cfg) { return payload; }

      const frameTypes = (cfg.frame_types || []).filter(function (ft) {
        return ft.frame_type_id === payload.frame_type_id;
      });
      const attributes = (cfg.attribute_types || []).map(function (a) {
        const slim = {};
        IS_ATTR_KEYS.forEach(function (k) { if (a[k] !== undefined) { slim[k] = a[k]; } });
        return slim;
      });

      const tpl = payload.template;
      return Object.assign({}, payload, {
        template: {
          // Identity of the template version. IntelliSense caches responses by the whole
          // payload, and two versions (v1.2 / v1.3) can share an identical frame type —
          // without this they would share cache entries.
          id:              tpl.id || null,
          templateVersion: tpl.templateVersion || tpl.version || null,
          templateConfig: {
            frame_types:     frameTypes,
            resource_types:  (cfg.resource_types || [])
                               .map(function (r) { return { id: r.id, name: r.name, description: r.description }; }),
            attribute_types: attributes
          }
        }
      });
    }

    function baseHeaders() {
      return { 'X-User-Account-Id': getAccountId() };
    }

    function authHeaders() {
      const h = baseHeaders();

      // $http.defaults.headers.common['Authorization'] is set automatically by
      // the Keycloak Angular adapter and is merged into every $http call.
      // For XHR / fetch we must read it explicitly.
      const defaultAuth = $http.defaults.headers.common &&
                          $http.defaults.headers.common['Authorization'];

      if (defaultAuth) {
        h['Authorization'] = defaultAuth;
      } else {
        // Fallback: some setups store the token directly in sessionStorage
        const bearer = getBearer();
        if (bearer) h['Authorization'] = bearer.startsWith('Bearer ') ? bearer : `Bearer ${bearer}`;
      }

      return h;
    }

    // ======================
    // LIST BY PROFILE
    // GET /?profile_id={profileId}&page=&size=&search=&sort_field=&sort_dir=&grammar=
    // ======================
    this.listByProfile = function (profileId, options = {}) {
      const params = {
        profile_id: profileId,
        page: options.page !== undefined ? options.page : 0,
        size: options.size !== undefined ? options.size : 10
      };

      if (options.search)      params.search       = options.search;
      if (options.sortField)   params.sort_field   = options.sortField;
      if (options.sortDir)     params.sort_dir     = options.sortDir;
      if (options.liveMode)    params.live_mode    = true;
      if (options.liveEnvIds)  params.live_env_ids = options.liveEnvIds;
      if (options.grammar)     params.grammar      = options.grammar;

      // No global spinner: the list auto-refreshes on an interval.
      return $http.get(BASE, { headers: authHeaders(), params, bmSkipLoader: true });
    };

    // ======================
    // GET ONE
    // GET /{bizEventId}
    // ======================
    this.getById = function (bizEventId) {
      return $http.get(
        `${BASE}/${bizEventId}`,
        { headers: authHeaders() }
      );
    };

    // ======================
    // CREATE
    // POST /
    // ======================
    this.create = function (payload) {
      return $http.post(
        BASE,
        payload,
        { headers: authHeaders() }
      );
    };

    // ======================
    // UPDATE
    // PUT /{bizEventId}/version/{version}
    // ======================
    this.update = function (bizEventId, payload) {
      const version = payload.biz_event_version;
      return $http.put(
        `${BASE}/${bizEventId}/version/${version}`,
        payload,
        { headers: authHeaders() }
      );
    };

    // ======================
    // PATCH
    // PATCH /{bizEventId}
    // ======================
    this.patch = function (bizEventId, payload) {
      return $http.patch(
        `${BASE}/${bizEventId}`,
        payload,
        { headers: authHeaders() }
      );
    };

    // ======================
    // DELETE
    // DELETE /{bizEventId}
    // ======================
    // ======================
    // CLONE — "Copy of <name>" with every version (created disabled)
    // POST /{bizEventId}/clone
    // ======================
    this.clone = function (bizEventId) {
      return $http.post(
        `${BASE}/${bizEventId}/clone`,
        null,
        { headers: authHeaders() }
      );
    };

    this.delete = function (bizEventId) {
      return $http.delete(
        `${BASE}/${bizEventId}`,
        { headers: authHeaders() }
      );
    };

    // ======================
    // BULK DELETE
    // DELETE /byIds
    // ======================
    this.bulkDelete = function (bizEventIds) {
      const headers = authHeaders();
      headers['Content-Type'] = 'application/json';
      return $http({
        method: 'DELETE',
        url: `${BASE}/byIds`,
        headers: headers,
        data: { bizEventIds: bizEventIds }
      });
    };

    // ======================
    // PATCH VERSION
    // PATCH /{bizEventId}/version/{versionString}  — singular, version string e.g. "1.0"
    // ======================
    this.patchVersion = function (bizEventId, versionString, payload) {
      return $http.patch(
        `${BASE}/${bizEventId}/version/${versionString}`,
        payload,
        { headers: authHeaders() }
      );
    };

    // ======================
    // REINSTATE VERSION
    // POST /{bizEventId}/version/{versionId}/reinstate
    // Re-instates a DEPRECATED version directly to PUBLISHED.
    // ======================
    this.reinstateVersion = function (bizEventId, versionId) {
      return $http.post(
        `${BASE}/${bizEventId}/version/${versionId}/reinstate`,
        {},
        { headers: authHeaders() }
      );
    };

    // ======================
    // DELETE VERSION
    // DELETE /{bizEventId}/version/{versionString}
    // ======================
    this.deleteVersion = function (bizEventId, versionString) {
      return $http.delete(
        `${BASE}/${bizEventId}/version/${versionString}`,
        { headers: authHeaders() }
      );
    };

    // ======================
    // PURGE RETIRED/DEPRECATED VERSIONS
    // POST /purge  — single server-side call, returns { purged_count, events_processed }
    // ======================
    this.purgeRetiredDeprecated = function (bizEventIds) {
      const headers = authHeaders();
      headers['Content-Type'] = 'application/json';
      return $http({
        method: 'POST',
        url: `${BASE}/purge`,
        headers: headers,
        data: { biz_event_ids: bizEventIds }
      });
    };

    // ======================
    // GET ALL NAMES — fetches all event names for a profile (no pagination).
    // Used by the wizard for client-side duplicate name validation.
    // ======================
    this.getAllNames = function (profileId) {
      return $http.get(BASE, {
        headers: authHeaders(),
        params:  { profile_id: profileId, size: 1000, page: 0 }
      }).then(function (response) {
        return ((response.data && response.data.content) || [])
          .map(function (e) {
            return (e.biz_event_name || '').trim().toLowerCase();
          });
      });
    };

    // ======================
    // GET ALL (full objects) — fetches every biz event of a profile, no
    // pagination. Used by the Biz Interceptor create/edit wizards' "Biz
    // Events" picker, which needs the full list (name/description/enabled)
    // to render checkboxes, not just names — same size:1000 idiom as
    // getAllNames above.
    // ======================
    // The biz events an interceptor of this transport can capture: REST_API
    // carries REST-API biz events only, every other transport the document
    // grammars (anything but REST-API) — same rule as the backend's
    // TransportType#accepts.
    this.filterByTransport = function (events, transportType) {
      var restApi = transportType === 'REST_API';
      return (events || []).filter(function (event) {
        var grammar = self.getBizEventGrammar(event);
        return !!grammar && (restApi ? grammar === self.REST_API_GRAMMAR : grammar !== self.REST_API_GRAMMAR);
      });
    };

    this.getAllForProfile = function (profileId) {
      return $http.get(BASE, {
        headers: authHeaders(),
        params:  { profile_id: profileId, size: 1000, page: 0 }
      }).then(function (response) {
        return (response.data && response.data.content) || [];
      });
    };

    // ======================
    // INTELLISENSE — legacy synchronous endpoint (kept for back-compat)
    // POST /v1/api/bizmetry-llm/intellisense/mapping
    // ======================
    this.suggestMappings = function (payload) {
      return $http.post(
        `${LLM_BASE}/mapping`,
        compactIntelliSensePayload(payload),
        { headers: authHeaders() }
      );
    };

    // ======================
    // INTELLISENSE — Step 1: start async job
    // POST {engine base}/start   (engine: INTELLISENSE_ENGINE.*, default DETERMINISTIC)
    //
    // payload = {
    //   operations:    [...],
    //   template:      {...},
    //   frame_type_id: "uuid",
    //   options: { threshold: 0.55, max_sources: 1 }   // optional
    // }
    //
    // Response: { job_id: "hex-string" }
    // ======================
    this.startIntelliSense = function (payload, engine) {
      return $http.post(
        `${_engine(engine).base}/start`,
        compactIntelliSensePayload(payload),
        { headers: authHeaders(), bmSkipLoader: true }
      );
    };

    // ======================
    // INTELLISENSE — cancel a running job
    // POST {agent base}/{jobId}/cancel — the agent stops before its next model call.
    // Only the agent supports it (deterministic jobs take seconds): a no-op otherwise.
    // Fire-and-forget: the client has already closed its progress dialog.
    // ======================
    this.cancelIntelliSense = function (jobId, engine) {
      if (!jobId || engine !== this.INTELLISENSE_ENGINE.AGENT) { return; }
      $http.post(
        `${_engine(engine).base}/${jobId}/cancel`, null,
        { headers: authHeaders(), bmSkipLoader: true }
      ).catch(angular.noop);
    };

    // ======================
    // DISMISS RECOMPUTE SUMMARY ("Recomputed" badge)
    // DELETE /versions/{versionId}/recompute-summary
    // ======================
    this.dismissRecomputeSummary = function (versionId) {
      return $http.delete(
        `${BASE}/versions/${versionId}/recompute-summary`,
        { headers: authHeaders(), bmSkipLoader: true }
      );
    };

    // ======================
    // INTELLISENSE AT WORK AFTER A RECOMPUTE
    // ======================
    // A template / API collection change can leave a version's targets unmapped; the backend then
    // asks IntelliSense (the agent, ~1 min) to map them and marks the version meanwhile with
    // recompute_summary.intellisense_pending_since. A mark older than this is stale (the run died).
    var INTELLISENSE_PENDING_MAX_MS = 6 * 60 * 1000;

    /** Epoch ms IntelliSense started on the version's unmapped targets, or null when it isn't at work. */
    this.intelliSensePendingSince = function (version) {
      var since = version && version.recompute_summary && version.recompute_summary.intellisense_pending_since;
      return since && (Date.now() - since) < INTELLISENSE_PENDING_MAX_MS ? since : null;
    };

    /**
     * Polls the biz event until IntelliSense is done with the version, then calls onDone(freshVersion)
     * once. Silent (no global loader). Returns a stop() function — call it when the view goes away.
     */
    this.watchIntelliSenseRecompute = function (bizEventId, versionId, onDone, intervalMs) {
      var self = this, inFlight = false;
      var timer = $interval(function () {
        if (inFlight) { return; }
        inFlight = true;
        $http.get(`${BASE}/${bizEventId}`, { headers: authHeaders(), bmSkipLoader: true })
          .then(function (res) {
            var versions = (res.data && res.data.versions) || [];
            var fresh = versions.find(function (v) { return v.biz_event_version_id === versionId; });
            if (!fresh || !self.intelliSensePendingSince(fresh)) {
              stop();
              onDone(fresh || null);
            }
          })
          .catch(function () { /* transient — next tick retries; the mark expires anyway */ })
          .finally(function () { inFlight = false; });
      }, intervalMs || 4000);
      function stop() { $interval.cancel(timer); }
      return stop;
    };

    // ======================
    // INTELLISENSE — Step 2: get current job status (single call)
    // GET {engine base}/{jobId}/status — same engine the job was started on
    //
    // Called by the controller in a chained $timeout (intelliSensePollInterval).
    // Returns: { status: "PENDING"|"RUNNING"|"COMPLETE"|"ERROR", percent, message, result? }
    // ======================
    this.pollIntelliSenseStatus = function (jobId, engine) {
      return $http.get(
        `${_engine(engine).base}/${jobId}/status`,
        {
          headers:      authHeaders(),
          bmSkipLoader: true   // suppress global spinner — interceptor checks this flag
        }
      );
    };

  }
]);